'use client';

import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
  useSyncExternalStore
} from 'react';

/**
 * What the header search bar searches, decided by the page you are on.
 *
 * A scope surfaces in the bar as a dismissible pill (`@jon`, `s/healthiii`,
 * `f/health`) — searches run inside it until you close the pill, which drops
 * back to searching the whole site.
 *
 * `label` is the pill's text and follows the app's existing token convention,
 * so a scope reads the same as the `@` / `s/` / `f/` prefixes used everywhere
 * else. Scopes carry the slug from the URL because that's what pages have; the
 * server resolves it (see `search.semantic`).
 */
export type SearchScope =
  | { kind: 'user'; username: string; label: string }
  // factiiiId drives the filter (a slug isn't unique across spaces); slug is
  // display only, and is what survives in a shared /search URL.
  | { kind: 'factiii'; factiiiId: number; slug: string; label: string }
  | { kind: 'space'; spaceSlug: string; label: string }
  // A board filters its own lists in place instead of handing off to /search,
  // so it never travels in a URL.
  | { kind: 'board'; spaceSlug: string; label: string };

export const userScope = (username: string): SearchScope => ({
  kind: 'user',
  username,
  label: `@${username}`
});

export const spaceScope = (spaceSlug: string): SearchScope => ({
  kind: 'space',
  spaceSlug,
  label: `s/${spaceSlug}`
});

export const boardScope = (spaceSlug: string): SearchScope => ({
  kind: 'board',
  spaceSlug,
  label: `s/${spaceSlug}`
});

export const factiiiScope = (factiiiId: number, slug: string): SearchScope => ({
  kind: 'factiii',
  factiiiId,
  slug,
  label: `f/${slug}`
});

/** The scope as `search.semantic` takes it (label/slug are client-side only). */
export const scopeInput = (scope: SearchScope) => {
  switch (scope.kind) {
    case 'user':
      return { kind: 'user' as const, username: scope.username };
    case 'factiii':
      return { kind: 'factiii' as const, factiiiId: scope.factiiiId };
    case 'space':
      return { kind: 'space' as const, spaceSlug: scope.spaceSlug };
    case 'board':
      // Board cards aren't in the vector index; BoardLayout queries board.cards.
      return undefined;
  }
};

/**
 * `?scope=` on /search, so a scoped search is shareable and survives a reload.
 * The factiii form carries both ids: the numeric one filters, the slug labels
 * the pill without a second round trip to look the name up.
 */
export const encodeScope = (scope: SearchScope): string | null => {
  switch (scope.kind) {
    case 'user':
      return `user:${scope.username}`;
    case 'space':
      return `space:${scope.spaceSlug}`;
    case 'factiii':
      return `factiii:${scope.factiiiId}:${scope.slug}`;
    case 'board':
      return null;
  }
};

export const decodeScope = (raw: string | null): SearchScope | null => {
  if (!raw) return null;
  const [kind, value, extra] = raw.split(':');
  if (kind === 'user' && value) return userScope(value);
  if (kind === 'space' && value) return spaceScope(value);
  if (kind === 'factiii' && value && extra) {
    const id = Number(value);
    if (Number.isInteger(id) && id > 0) return factiiiScope(id, extra);
  }
  return null;
};

type State = {
  scope: SearchScope | null;
  query: string;
  // Which scope the user closed the pill on. Kept so the page re-registering
  // the same scope can't quietly bring it back; a different page clears it.
  dismissed: string | null;
};

type Action =
  | { type: 'scope'; scope: SearchScope | null }
  | { type: 'query'; query: string }
  | { type: 'dismiss' };

// Identity of a scope by value, so a page re-rendering (or refetching its
// data) doesn't read as "the user navigated somewhere else".
export const scopeKey = (scope: SearchScope | null) => {
  if (!scope) return '';
  switch (scope.kind) {
    case 'user':
      return `user:${scope.username}`;
    case 'factiii':
      return `factiii:${scope.factiiiId}`;
    default:
      return `${scope.kind}:${scope.spaceSlug}`;
  }
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'scope': {
      const key = scopeKey(action.scope);
      // Same page re-registering: keep whatever the user has already typed,
      // and keep the pill closed if they closed it. A different page starts
      // fresh — carrying either over would apply to a page they have left.
      if (scopeKey(state.scope) === key && state.dismissed === null)
        return state;
      if (action.scope && state.dismissed === key) return state;
      return { scope: action.scope, query: '', dismissed: null };
    }
    case 'query':
      return { ...state, query: action.query };
    case 'dismiss':
      return { scope: null, query: '', dismissed: scopeKey(state.scope) };
  }
};

type ContextValue = State & { dispatch: Dispatch<Action> };

const SearchScopeContext = createContext<ContextValue>({
  scope: null,
  query: '',
  dismissed: null,
  dispatch: () => undefined
});

export const SearchScopeProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(reducer, {
    scope: null,
    query: '',
    dismissed: null
  });

  return (
    <SearchScopeContext.Provider value={{ ...state, dispatch }}>
      {children}
    </SearchScopeContext.Provider>
  );
};

const subscribeToNothing = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;

export const useSearchScope = (): ContextValue => {
  const value = useContext(SearchScopeContext);
  // A scoped page sets its scope in an Effect, which can run before a
  // Suspense boundary that reads it (the header, its search bar) hydrates;
  // the server rendered no scope, so the hydrating render must not see one
  // either. `hydrated` is the server snapshot (false) for that render and
  // true on the synchronous pass straight after.
  const hydrated = useSyncExternalStore(
    subscribeToNothing,
    clientSnapshot,
    serverSnapshot
  );
  return hydrated ? value : { ...value, scope: null };
};

/**
 * Point the header search bar at this page for as long as it is mounted.
 * Pass `null` while the page is still loading the data the scope needs.
 */
export const useRegisterSearchScope = (scope: SearchScope | null) => {
  const { dispatch } = useSearchScope();

  // Key the effect off the scope's value: callers build the object inline, so
  // its identity changes on every render even when nothing about it has.
  // `dispatch` is stable, so the effect only ever re-runs on a real change.
  const serialized = scope ? JSON.stringify(scope) : '';

  useEffect(() => {
    dispatch({
      type: 'scope',
      scope: serialized ? (JSON.parse(serialized) as SearchScope) : null
    });
    return () => dispatch({ type: 'scope', scope: null });
  }, [serialized, dispatch]);
};
